/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { darken, lighten } from '@mui/material/styles';
import { DownloadSeriesPoint } from 'openvsx-webui';
import { RegistryExtensionSeries } from './analytics-types';
import { displayBucket } from './analytics-buckets';

/** How many extensions can be compared at once. */
export const COMPARE_MAX = 4;

/** One compared extension: a 'namespace.name' reference, optionally pinned to a version. */
export interface CompareEntry {
  ref: string;
  version?: string;
}

/** Parses a comparison-pool token ('ns.name', optionally '@version'-pinned). */
export function parseCompareToken(token: string): CompareEntry {
  const separator = token.indexOf('@');
  return separator >= 0
    ? { ref: token.slice(0, separator), version: token.slice(separator + 1) || undefined }
    : { ref: token };
}

export function formatCompareToken(entry: CompareEntry): string {
  return entry.version ? `${entry.ref}@${entry.version}` : entry.ref;
}

/** The 'namespace.name' reference of an extension. */
export function refOf(extension: { namespace: string; name: string }): string {
  return `${extension.namespace}.${extension.name}`;
}

/**
 * Splits a 'namespace.name' reference at the first dot — extension names may contain dots.
 * A reference without a dot (a hand-edited URL) has no namespace; slicing at -1 would instead
 * invent one out of the name's own characters.
 */
export function splitExtensionRef(ref: string): [string, string] {
  const separator = ref.indexOf('.');
  return separator < 0 ? ['', ref] : [ref.slice(0, separator), ref.slice(separator + 1)];
}

/** Palette the per-extension spark colors are drawn from. */
export const SERIES_PALETTE = [
  '#8b5cf6',
  '#0ea5e9',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#6366f1',
  '#14b8a6',
  '#d946ef',
  '#f97316',
  '#84cc16'
];

/** Stable pseudo-random color for an extension reference, so rows keep their color across renders. */
export function seriesColor(ref: string): string {
  let hash = 0;
  for (const char of ref) {
    hash = (hash * 31 + char.charCodeAt(0)) | 0;
  }
  return SERIES_PALETTE[Math.abs(hash) % SERIES_PALETTE.length];
}

/** Relative luminance (0..1) of a '#rgb'/'#rrggbb' color, or undefined when unparseable. */
function hexLuminance(color: string): number | undefined {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!match) {
    return undefined;
  }
  const hex = match[1].length === 3 ? [...match[1]].map((char) => char + char).join('') : match[1];
  const [r, g, b] = [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * The extension's own gallery color when readable on the current background (gallery colors
 * are often near-black), otherwise a stable invented one. Most extensions define no gallery
 * color, which the API reports as null rather than as an absent field.
 */
export function pickSeriesColor(ref: string, defined: string | null | undefined, darkMode: boolean): string {
  if (defined != null) {
    const luminance = hexLuminance(defined);
    if (luminance !== undefined && (darkMode ? luminance >= 0.18 : luminance <= 0.82)) {
      return defined;
    }
  }
  return seriesColor(ref);
}

/**
 * Shades of an extension's accent for its version bands, oldest first: the newest keeps the
 * pure accent, older ones fade toward the background (lighter in light, darker in dark mode).
 */
export function versionShades(accent: string, count: number, darkMode: boolean): string[] {
  const fade = darkMode ? 0.55 : 0.72;
  return Array.from({ length: count }, (_, index) => {
    const t = count <= 1 ? 0 : ((count - 1 - index) / (count - 1)) * fade;
    return t === 0 ? accent : darkMode ? darken(accent, t) : lighten(accent, t);
  });
}

/** Shade per version of a newest-first list; versions beyond `steps` share the faintest shade. */
export function versionShadeMap(versions: string[], accent: string, darkMode: boolean, steps = 8): Map<string, string> {
  const count = Math.min(versions.length, steps);
  const shades = versionShades(accent, count, darkMode).reverse();
  return new Map(versions.map((version, index) => [version, shades[Math.min(index, count - 1)]]));
}

/** View model of one compared extension, built from the doubled series response. */
export interface CompareColumn extends CompareEntry {
  /** Display name from the series response, falling back to the extension name. */
  name: string;
  /** Line/bar color: the extension's own gallery color when readable, de-duplicated across columns. */
  color: string;
  iconUrl?: string;
  averageRating?: number;
  reviewCount?: number;
  /** Current-period downloads per bucket, aligned with `labels`. */
  counts: number[];
  labels: string[];
  /** Total downloads of the current period. */
  total: number;
  /** Total downloads of the preceding period of equal length (the growth baseline). */
  previous: number;
  /** Versions published within the shown period, for release markers. */
  releases: { version: string; date: string }[];
}

/**
 * Folds the comparison's series responses into one column per compared extension: the current
 * period's points, the baseline period's total for growth figures, and a distinct color each.
 */
export function buildCompareColumns(
  compared: CompareEntry[],
  series: readonly RegistryExtensionSeries[],
  baseline: readonly RegistryExtensionSeries[],
  darkMode: boolean
): CompareColumn[] {
  // the server resolves references case-insensitively and answers in the canonical casing, so
  // a deep link written in the marketplace's casing must still find its series
  const byRef = (entries: readonly RegistryExtensionSeries[]) =>
    new Map(entries.map((entry) => [refOf(entry).toLowerCase(), entry]));
  const currentByRef = byRef(series);
  const baselineByRef = byRef(baseline);
  const usedColors = new Set<string>();
  return compared.map(({ ref, version }) => {
    const entry = currentByRef.get(ref.toLowerCase());
    const current = entry?.points ?? [];
    const previous = baselineByRef.get(ref.toLowerCase())?.points ?? [];
    let color = pickSeriesColor(ref, entry?.color, darkMode);
    if (usedColors.has(color)) {
      color = SERIES_PALETTE.find((candidate) => !usedColors.has(candidate)) ?? color;
    }
    usedColors.add(color);
    return {
      ref,
      version,
      // prefer what the response carries — it is canonical, the ref is whatever the URL said
      name: entry?.displayName ?? entry?.name ?? splitExtensionRef(ref)[1],
      color,
      // the response spells an absent field null; the column keeps plain optionals
      iconUrl: entry?.iconUrl ?? undefined,
      averageRating: entry?.averageRating ?? undefined,
      reviewCount: entry?.reviewCount ?? undefined,
      counts: current.map((point) => point.count),
      labels: current.map((point) => displayBucket(point.t)),
      total: sumCounts(current),
      previous: sumCounts(previous),
      // the server keeps these to the requested, i.e. shown, period
      releases: entry?.releases ?? []
    };
  });
}

/** A column's average downloads per bucket, or undefined for an empty period. */
export function columnAverage(column: CompareColumn): number | undefined {
  return column.counts.length > 0 ? Math.round(column.total / column.counts.length) : undefined;
}

/** A column's highest bucket with its label, or undefined for an empty period. */
export function columnPeak(column: CompareColumn): { count: number; label: string } | undefined {
  if (column.counts.length === 0) {
    return undefined;
  }
  let peakIndex = 0;
  for (let index = 1; index < column.counts.length; index++) {
    if (column.counts[index] > column.counts[peakIndex]) {
      peakIndex = index;
    }
  }
  return { count: column.counts[peakIndex], label: column.labels[peakIndex] };
}

export function sumCounts(points: DownloadSeriesPoint[]): number {
  return points.reduce((sum, point) => sum + point.count, 0);
}

/** Relative growth against a previous total, as a signed label ("+12.5%"); undefined without a baseline. */
export function growthLabel(current: number, previous: number): string | undefined {
  if (previous <= 0) {
    return undefined;
  }
  const pct = ((current - previous) / previous) * 100;
  const rounded = Math.abs(pct) >= 100 ? Math.round(pct) : Math.round(pct * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

/**
 * Tone of a growth label: rising, falling, or flat. Zero movement (and missing baselines)
 * earns no color — only real change gets the green/red treatment.
 */
export function growthTone(delta: string | undefined): 'up' | 'down' | 'flat' {
  if (delta === undefined) {
    return 'flat';
  }
  if (delta.startsWith('-')) {
    return 'down';
  }
  // growthLabel signs every positive value; unsigned means exactly zero
  return delta.startsWith('+') ? 'up' : 'flat';
}

/** Theme color path of a growth label, for `sx` color props. */
export function growthColor(delta: string | undefined): string {
  switch (growthTone(delta)) {
    case 'up':
      return 'success.main';
    case 'down':
      return 'error.main';
    case 'flat':
      return 'text.disabled';
  }
}
